/**
 * PB-FIELD-PARITY field runtime registry — render/init/validate/hydrate/flush hooks per field type.
 * PB-FP-1: simple adapters (Text, Long text, Choice/Button) + foundation API. Flag-off by default.
 */
import '@theme/pb-text-reveal';
import { syncStoreField } from '@theme/pb-store-field';
import { mountStoreMedia } from '@theme/pb-store-media';
import { resolveVariantImageSource } from '@theme/pb-variant-media';
import { createAiImageAdapter as createAiImageAdapterFromPlugin } from '@theme/pb-field-ai-image';
import { createAiTextAdapter as createAiTextAdapterFromPlugin } from '@theme/pb-field-ai-text';
import {
  extractFieldTypeLabel,
  hasAiTextSettingsSignature,
  isAiTextFieldMisconfigured,
  isAiTextFieldType,
  parseAiTextFieldSettings,
  resolveAiTextLayoutMode,
  AI_TEXT_LAYOUT_CONTENT_ONLY,
} from '@theme/pb-ai-text-logic';
import { createAudioAdapter as createAudioAdapterFromPlugin, buildAudioMicSvg as buildAudioMicSvgFromPlugin, AUDIO_MIC_PATH_D as AUDIO_MIC_PATH_D_FROM_PLUGIN } from '@theme/pb-field-audio';
import { createChoiceAdapter as createChoiceAdapterFromPlugin } from '@theme/pb-field-choice';
import { createDerivedPropertiesAdapter as createDerivedPropertiesAdapterFromPlugin } from '@theme/pb-field-derived-properties';
import { createDesignLibraryAdapter as createDesignLibraryAdapterFromPlugin } from '@theme/pb-field-design-library';
import {
  finalizeDerivedProperties,
  isDerivedPropertiesField,
  isDerivedSourceLabel,
  validateDerivedSourceValue,
} from '@theme/pb-derived-properties-logic';
import { createLongTextAdapter as createLongTextAdapterFromPlugin, buildLongTextFieldHtml as buildLongTextFieldHtmlFromPlugin } from '@theme/pb-field-long-text';
import { createTextAdapter as createTextAdapterFromPlugin } from '@theme/pb-field-text';
import { createTopicMultiAdapter as createTopicMultiAdapterFromPlugin } from '@theme/pb-field-topic-multi';
import { createUploadAdapter as createUploadAdapterFromPlugin } from '@theme/pb-field-upload';

export { buildLongTextFieldHtmlFromPlugin as buildLongTextFieldHtml };
export const AUDIO_MIC_PATH_D = AUDIO_MIC_PATH_D_FROM_PLUGIN;
export const buildAudioMicSvg = buildAudioMicSvgFromPlugin;
export const AUDIO_MIC_SVG = buildAudioMicSvgFromPlugin();

export const FIELD_MODES = Object.freeze({
  MAIN: 'MAIN',
  SUB: 'SUB',
});

export const FIELD_FLOWS = Object.freeze({
  FRESH: 'fresh',
  CART_EDIT: 'cart_edit',
  DRAFT_RESUME: 'draft_resume',
  BUNDLE_ROW_EDIT: 'bundle_row_edit',
});

export class UnsupportedFieldTypeError extends Error {
  constructor(fieldType) {
    super('Unsupported field type: ' + String(fieldType || 'unknown'));
    this.name = 'UnsupportedFieldTypeError';
    this.fieldType = fieldType;
  }
}

export function isUnifiedFieldEngineEnabled(data) {
  const source = data || (typeof window !== 'undefined' ? window.BUILDER_DATA : null);
  return !!(source && source.unifiedFieldEngine === true);
}

export function escapeHtmlAttr(str) {
  return String(str ?? '')
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function settingsToString(raw) {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'object') {
    if (typeof raw.value === 'string') return raw.value;
    try {
      return JSON.stringify(raw);
    } catch (e) {
      return String(raw);
    }
  }
  return String(raw);
}

export function extractChoices(raw, settingsStr) {
  const str = settingsStr != null ? String(settingsStr) : settingsToString(raw);
  return String(str || '')
    .split(',')
    .map(function (c) {
      return c.trim();
    })
    .filter(function (c) {
      return c && !c.includes(':');
    });
}

function parseBoolSetting(val, defaultVal) {
  if (val == null || val === '') return defaultVal;
  return val === true || ['true', 'on'].includes(String(val).toLowerCase());
}

function firstSetting(raw, keys) {
  for (let i = 0; i < keys.length; i += 1) {
    if (raw[keys[i]] != null && raw[keys[i]] !== '') return raw[keys[i]];
  }
  return undefined;
}

function buildSettingsFromObject(raw) {
  const minVal = firstSetting(raw, ['min', 'minLength', 'answer_min']) ?? 0;
  const maxVal = firstSetting(raw, ['max', 'maxLength', 'answer_max']) ?? 100;
  const recorderVal = firstSetting(raw, ['recorder', 'answer_recorder']);
  const out = {
    required: parseBoolSetting(firstSetting(raw, ['required', 'answer_required']), false),
    uppercase: parseBoolSetting(raw.uppercase, false),
    min: parseInt(minVal || 0, 10),
    max: parseInt(maxVal || 100, 10),
    placeholder: firstSetting(raw, ['placeholder', 'answer_placeholder']) || '',
    recorder: parseBoolSetting(recorderVal, false),
    recorder_max:
      parseInt(firstSetting(raw, ['recorder_max', 'answer_recorder_max']) || 300, 10) || 300,
  };
  if (raw.max_file_size != null) out.max_file_size = parseInt(raw.max_file_size, 10) || 20;
  if (raw.max_files != null) out.max_files = parseInt(raw.max_files, 10) || 10;
  if (raw.accept != null) out.accept = String(raw.accept).trim().toLowerCase();
  if (raw.photo_property != null) out.photo_property = String(raw.photo_property).trim();
  return out;
}

function firstRegexMatch(settings, patterns, fallback) {
  for (let i = 0; i < patterns.length; i += 1) {
    const match = settings.match(patterns[i]);
    if (match) return match[1];
  }
  return fallback;
}

export function parseSettings(raw) {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    return buildSettingsFromObject(raw);
  }
  const settings = String(raw || '');
  if (settings.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(settings);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parseSettings(parsed);
      }
    } catch (e) {
      /* fall through */
    }
  }
  const maxFileSizeMatch = settings.match(/max_file_size\s*:\s*(\d+)/i);
  const maxFilesMatch = settings.match(/max_files\s*:\s*(\d+)/i);
  const acceptMatch = settings.match(/accept\s*:\s*([^,]+(?:,\s*[^,]+)?)/i);
  const photoPropMatch = settings.match(/photo_property\s*:\s*([^,\n]+)/i);
  const required =
    /required\s*:\s*true/i.test(settings) || /answer_required\s*:\s*true/i.test(settings);
  const recorder =
    /recorder\s*:\s*true/i.test(settings) ||
    /recorder\s*:\s*on/i.test(settings) ||
    /answer_recorder\s*:\s*true/i.test(settings) ||
    /answer_recorder\s*:\s*on/i.test(settings);
  return {
    required,
    uppercase: /uppercase\s*:\s*true/i.test(settings),
    min: parseInt(
      firstRegexMatch(settings, [/min\s*:\s*(\d+)/i, /answer_min\s*:\s*(\d+)/i], '0'),
      10
    ),
    max: parseInt(
      firstRegexMatch(settings, [/max\s*:\s*(\d+)/i, /answer_max\s*:\s*(\d+)/i], '100'),
      10
    ),
    placeholder:
      settings.match(/placeholder\s*:\s*([^,]+)/i)?.[1] ||
      settings.match(/answer_placeholder\s*:\s*([^,]+)/i)?.[1] ||
      '',
    recorder,
    recorder_max:
      parseInt(
        firstRegexMatch(
          settings,
          [/recorder_max\s*:\s*(\d+)/i, /answer_recorder_max\s*:\s*(\d+)/i],
          '300'
        ),
        10
      ) || 300,
    max_file_size: maxFileSizeMatch ? parseInt(maxFileSizeMatch[1], 10) || 20 : 20,
    max_files: maxFilesMatch ? parseInt(maxFilesMatch[1], 10) || 10 : 10,
    accept: acceptMatch ? String(acceptMatch[1]).trim().toLowerCase().replace(/\s/g, '') : '',
    photo_property: photoPropMatch ? String(photoPropMatch[1]).trim() : '',
  };
}

/**
 * Authoritative text/long-text validity — sets input.dataset.valid and returns boolean.
 * Locked rule: text-only enforces min; valid optional audio may waive min text.
 */
function resolveActiveFieldConfigForValidation() {
  if (typeof window === 'undefined') return [];
  const bs = window.builderState;
  if (bs?.mode === 'SUB') {
    return bs.sub?.currentConfig || [];
  }
  const data = window.BUILDER_DATA;
  if (data?.shopifyBundle === true && Array.isArray(data.shopifyBundleMergedPersonalization)) {
    return data.shopifyBundleMergedPersonalization;
  }
  return data?.mainProduct?.config || data?.mainProduct?.personalization || [];
}

export function validateTextInput(input, settings) {
  if (!input) return true;
  const s = settings || parseSettings(input.dataset.settings || '');
  const val = String(input.value || '').trim();
  const wrap = input.closest('.pb-longtext-wrap');
  const audioInput = wrap ? wrap.querySelector('.pb-lt-audio-input') : null;
  const hasAudio = !!(
    audioInput &&
    String(audioInput.value || '').trim() &&
    audioInput.dataset.valid !== 'false'
  );

  let isValid = true;
  if (s.required && val.length === 0 && !hasAudio) isValid = false;
  else if (!hasAudio && val.length > 0 && (val.length < s.min || val.length > s.max)) isValid = false;
  else if (hasAudio && val.length > 0 && val.length > s.max) isValid = false;

  if (isValid) {
    const label = input.dataset?.label || '';
    const fieldConfig = resolveActiveFieldConfigForValidation();
    if (label && val.length > 0 && isDerivedSourceLabel(label, fieldConfig)) {
      if (!validateDerivedSourceValue(val)) isValid = false;
    }
  }

  // Keep the builder's existing counter/hint presentation; validation must not add inline copy.
  syncStoreField(input, { invalid: !isValid, error: '' });
  input.dataset.valid = isValid ? 'true' : 'false';
  input.dataset.required = s.required ? 'true' : 'false';
  return isValid;
}

/** Choice/button grid validity — sets grid.dataset.valid and returns boolean. */
export function validateChoiceGrid(grid, settings) {
  if (!grid) return true;
  const s = settings || parseSettings(grid.dataset?.settings || '');
  if (s.required) {
    grid.dataset.required = 'true';
    let hasSelection = grid.classList.contains('pb-topic-multi')
      ? !!grid.querySelector('.pb-topic-checkbox:checked')
      : !!grid.querySelector('.active');
    if (hasSelection) {
      const otherBtn = grid.querySelector('.meta-btn.active[data-other="true"]');
      if (otherBtn) {
        const step = grid.closest('.pb-step');
        let wrap = null;
        if (step) {
          step.querySelectorAll('.pb-other-input-wrap').forEach(function (w) {
            if (w.dataset.label === otherBtn.dataset.label) wrap = w;
          });
        }
        const input = wrap ? wrap.querySelector('input.pb-other-input') : null;
        if (!input || !String(input.value || '').trim()) hasSelection = false;
      }
    }
    grid.dataset.valid = hasSelection ? 'true' : 'false';
  } else {
    grid.dataset.required = 'false';
    grid.dataset.valid = 'true';
  }
  return grid.dataset.valid === 'true';
}

/** Topic Multi answer step — text limits and/or standalone audio on dynamic topic steps. */
export function validateTopicAnswerStep(stepEl) {
  if (!stepEl) return true;
  const audioWrap = stepEl.querySelector('.pb-audio-wrap');
  if (audioWrap) {
    const hidden = audioWrap.querySelector('input.builder-required[data-label]');
    if (!hidden || hidden.dataset.required !== 'true') return true;
    return hidden.dataset.valid === 'true' && !!String(hidden.value || '').trim();
  }
  const input = stepEl.querySelector(
    '.pb-topic-answer, .pb-long-text[data-label], textarea.builder-input[data-label]'
  );
  if (!input) return true;
  return validateTextInput(input, parseSettings(input.dataset.settings || ''));
}

/** Topic Multi parent step — topic checkbox grid required/min/max via shared choice gate. */
export function validateTopicMultiSelectionStep(stepEl) {
  if (!stepEl) return true;
  const grid = stepEl.querySelector('[data-topic-list]');
  if (!grid) return true;
  return validateChoiceGrid(grid);
}

/** Unified Topic Multi gate — parent selection or child answer step. */
export function validateTopicMultiGate(stepEl) {
  if (!stepEl) return true;
  if (stepEl.dataset?.topicParent) return validateTopicAnswerStep(stepEl);
  if (stepEl.dataset?.topicMulti === 'true') return validateTopicMultiSelectionStep(stepEl);
  return true;
}

function resolveAdapterSettings(stepEl, ctx, selector, fallbackSelector) {
  const parseFn = resolveRuntimeParseSettings(ctx);
  const el =
    (stepEl && stepEl.querySelector(selector)) ||
    (fallbackSelector && stepEl && stepEl.querySelector(fallbackSelector));
  if (!el) return null;
  const settingsStr =
    el.dataset.settings ||
    (ctx && ctx.field && ctx.field.settings != null ? settingsToString(ctx.field.settings) : '');
  return { el, settings: parseFn(settingsStr) };
}

function uploadAcceptAttr(settings) {
  const uploadAccept = settings && settings.accept ? String(settings.accept) : 'photo,video';
  if (uploadAccept.indexOf('video') !== -1 && uploadAccept.indexOf('photo') !== -1) {
    return 'image/*,video/*';
  }
  if (uploadAccept.indexOf('video') !== -1) return 'video/*';
  return 'image/*';
}

function resolveRuntimeParseSettings(ctx) {
  if (ctx && typeof ctx.parseSettings === 'function') return ctx.parseSettings.bind(ctx);
  if (typeof window !== 'undefined' && window.productBuilder?.parseSettings) {
    return window.productBuilder.parseSettings.bind(window.productBuilder);
  }
  return parseSettings;
}

function delegateInitStep(stepEl, selector, initFn) {
  if (typeof window === 'undefined' || !stepEl) return;
  const el = stepEl.querySelector(selector);
  if (!el) return;
  initFn(el, stepEl);
}

function showFieldInteractionMessage(wrap, kind, text, isError) {
  if (!wrap) return;
  const classBase = kind === 'audio' ? 'pb-audio-message' : 'pb-upload-message';
  const msg = wrap.querySelector('.' + classBase);
  if (!msg) return;
  msg.textContent = text || '';
  msg.style.display = text ? 'block' : 'none';
  msg.className = classBase + (isError ? ' ' + classBase + '-error' : '');
}

function bindClickOnce(el, handler) {
  if (!el || el.dataset.pbBound === 'true') return;
  el.dataset.pbBound = 'true';
  el.addEventListener('click', handler);
}

export function bindUploadWrapInteractions(wrap) {
  if (!wrap || wrap.dataset.pbUploadBound === 'true') return;
  wrap.dataset.pbUploadBound = 'true';

  const pb = typeof window !== 'undefined' ? window.productBuilder : null;
  const triggerReady = pb && typeof pb.triggerUploadInput === 'function';
  const handleReady = pb && typeof pb.handleFiles === 'function';
  if (!triggerReady || !handleReady) {
    showFieldInteractionMessage(
      wrap,
      'upload',
      'Upload module is not loaded. Please refresh and try again.',
      true
    );
    const emptyBtn = wrap.querySelector('.pb-upload-empty');
    if (emptyBtn) emptyBtn.disabled = true;
    return;
  }

  const fileInput = wrap.querySelector('.pb-upload-file-input');
  if (fileInput) {
    fileInput.addEventListener('change', function onUploadFilesChange() {
      pb.handleFiles(wrap, this.files);
      this.value = '';
    });
  }

  const emptyBtn = wrap.querySelector('.pb-upload-empty');
  if (emptyBtn) {
    bindClickOnce(emptyBtn, function onUploadTriggerClick() {
      pb.triggerUploadInput(this);
    });
  }
}

export function bindAudioWrapInteractions(wrap) {
  if (!wrap || wrap.dataset.pbAudioBound === 'true') return;
  wrap.dataset.pbAudioBound = 'true';

  const pb = typeof window !== 'undefined' ? window.productBuilder : null;
  const audioReady =
    pb &&
    typeof pb.startAudioRecording === 'function' &&
    typeof pb.playAudioRecording === 'function' &&
    typeof pb.clearAudioRecording === 'function';
  if (!audioReady) {
    showFieldInteractionMessage(
      wrap,
      'audio',
      'Audio recorder module is not loaded. Please refresh and try again.',
      true
    );
    const recordBtn = wrap.querySelector('.pb-audio-btn-main');
    if (recordBtn) recordBtn.disabled = true;
    return;
  }

  if (typeof window !== 'undefined' && window.pbAudioRecorder?.init) {
    window.pbAudioRecorder.init(wrap.closest('#main-flow-container, #sub-flow-container') || wrap);
  }

  const recordBtn = wrap.querySelector('.pb-audio-btn-main');
  if (recordBtn) {
    bindClickOnce(recordBtn, function onAudioRecordClick() {
      pb.startAudioRecording(this);
    });
  }
  const playBtn = wrap.querySelector('.pb-audio-btn-play');
  if (playBtn) {
    bindClickOnce(playBtn, function onAudioPlayClick() {
      pb.playAudioRecording(this);
    });
  }
  const deleteBtn = wrap.querySelector('.pb-audio-btn-delete');
  if (deleteBtn) {
    bindClickOnce(deleteBtn, function onAudioDeleteClick() {
      pb.clearAudioRecording(this);
    });
  }
}

function fieldTypeLower(field) {
  return extractFieldTypeLabel(field).toLowerCase();
}

function resolveEsc(ctx) {
  return (ctx && typeof ctx.esc === 'function' && ctx.esc) || escapeHtmlAttr;
}

function resolveSelections(ctx) {
  return (ctx && ctx.selections) || {};
}

function resolveProductRef(ctx) {
  return (ctx && ctx.productRef) || {};
}

function resolveToken(ctx, value) {
  if (ctx && typeof ctx.resolveToken === 'function') {
    return ctx.resolveToken(value);
  }
  return value;
}

function renderUnsupportedPanel(field, esc) {
  const label = esc((field && field.label) || 'Field');
  const type = esc(extractFieldTypeLabel(field) || 'unknown');
  return (
    '<div class="pb-field-unsupported" data-field-label="' +
    label +
    '" data-field-type="' +
    type +
    '">' +
    '<p class="pb-field-unsupported-msg">This personalization field is not supported yet.</p>' +
    '</div>'
  );
}

function renderAiTextMisconfigPanel(field, esc) {
  const label = esc((field && field.label) || 'Field');
  const type = esc(extractFieldTypeLabel(field) || 'unknown');
  return (
    '<div class="pb-field-unsupported pb-field-ai-text-misconfig" data-field-label="' +
    label +
    '" data-field-type="' +
    type +
    '">' +
    '<p class="pb-field-unsupported-msg">This step is not set up correctly yet.</p>' +
    '<p class="pb-field-unsupported-hint">Staff: open personalization field <strong>' +
    label +
    '</strong> in Shopify Admin and set <strong>Type</strong> to <strong>AI Text</strong> (current type: ' +
    type +
    '). Save, then hard-refresh the product page.</p>' +
    '</div>'
  );
}

function renderQuestionHighlight(field, productRef, esc) {
  const bg = esc(productRef.image || '');
  const question = esc(field.question || '');
  const descRaw = field.description;
  const descStr =
    descRaw != null && typeof descRaw === 'object'
      ? descRaw.value ?? descRaw.content ?? descRaw.text ?? ''
      : typeof descRaw === 'string'
        ? descRaw
        : String(descRaw || '');
  const descTrimmed = String(descStr || '').trim();
  const descHtml = descTrimmed
    ? '<div class="pb-question-highlight-desc" data-pb-template="' +
      esc(descTrimmed) +
      '">' +
      esc(descTrimmed) +
      '</div>'
    : '';
  return (
    '<div class="pb-question-highlight" style="--pb-highlight-bg: url(\'' +
    bg +
    '\');">' +
    '<div class="pb-question-highlight-bg"></div>' +
    '<div class="pb-question-highlight-inner">' +
    '<span class="pb-question-highlight-text pb-preview-display" data-pb-template="' +
    question +
    '">' +
    question +
    '</span>' +
    descHtml +
    '</div>' +
    '</div>'
  );
}

function renderImageColumn(field, ctx) {
  const esc = resolveEsc(ctx);
  const productRef = resolveProductRef(ctx);
  const visRaw = String((field && field.visual) || '').trim();
  if (!visRaw) {
    return renderQuestionHighlight(field, productRef, esc);
  }
  if (visRaw.includes('http') && !visRaw.includes('<')) {
    return (
      '<div class="pb-guide-image-wrap">' +
      '<img src="' +
      esc(visRaw) +
      '" class="pb-guide-image" alt="Visual Guide" width="300" height="300" loading="lazy">' +
      '</div>'
    );
  }
  return (
    '<div class="pb-guide-image-wrap">' +
    '<iframe srcdoc="' +
    esc(visRaw) +
    '" class="custom-html-visual" sandbox="allow-scripts allow-same-origin"></iframe>' +
    '</div>'
  );
}

/** Blurred question bg fills pane; instructional visual_content stays content-sized (PB-FND-28). */
export function resolvePreviewLayout(field) {
  const visRaw = String((field && field.visual) || '').trim();
  return visRaw ? 'guide' : 'fill';
}

function applyFieldLayoutContract(field, stepEl, imgMount) {
  const layout = resolvePreviewLayout(field);
  if (imgMount && imgMount.dataset) {
    imgMount.dataset.pbLayout = layout;
    if (imgMount.classList) {
      if (typeof imgMount.classList.remove === 'function') {
        imgMount.classList.remove('pb-field-layout-fill', 'pb-field-layout-guide');
      }
      if (typeof imgMount.classList.add === 'function') {
        imgMount.classList.add(layout === 'fill' ? 'pb-field-layout-fill' : 'pb-field-layout-guide');
      }
    }
  }
  if (stepEl) {
    const imgStep =
      stepEl.id &&
      String(stepEl.id).startsWith('content-page-') &&
      typeof document !== 'undefined' &&
      typeof document.getElementById === 'function'
        ? document.getElementById(String(stepEl.id).replace('content-page-', 'img-page-'))
        : null;
    if (imgStep && imgStep.classList && typeof imgStep.classList.toggle === 'function') {
      imgStep.classList.toggle('pb-field-layout-fill-step', layout === 'fill');
      imgStep.classList.toggle('pb-field-layout-guide-step', layout === 'guide');
    }
    const isAiText =
      field && (isAiTextFieldType(field) || hasAiTextSettingsSignature(field));
    if (isAiText) {
      const cfg = parseAiTextFieldSettings(field.settings);
      const contentOnly = resolveAiTextLayoutMode(cfg) === AI_TEXT_LAYOUT_CONTENT_ONLY;
      if (stepEl.classList && typeof stepEl.classList.toggle === 'function') {
        stepEl.classList.toggle('pb-step-ai-text-content-only', contentOnly);
      }
      if (imgStep && imgStep.classList && typeof imgStep.classList.toggle === 'function') {
        imgStep.classList.toggle('pb-step-ai-text-content-only', contentOnly);
      }
      if (stepEl.dataset) {
        stepEl.dataset.aiTextLayout = contentOnly ? AI_TEXT_LAYOUT_CONTENT_ONLY : 'controls-visible';
      }
    }
  }
}

function renderControlShell(field, innerHtml, ctx, options) {
  const esc = resolveEsc(ctx);
  const opts = options || {};
  const useQuestionHighlight = opts.useQuestionHighlight !== false;
  const questionSpan = useQuestionHighlight
    ? '<span class="option-label"><span class="pb-question-text" data-pb-template="' +
      esc(field.question || '') +
      '">' +
      esc(field.question || '') +
      '</span></span>'
    : '<span class="option-label" data-pb-template="' +
      esc(field.question || '') +
      '" data-pb-transform="upper">' +
      esc(String(field.question || '').toUpperCase()) +
      '</span>';
  const descRaw = field.description;
  const descStr =
    descRaw != null && typeof descRaw === 'object'
      ? descRaw.value ?? descRaw.content ?? descRaw.text ?? ''
      : typeof descRaw === 'string'
        ? descRaw
        : String(descRaw || '');
  const descTrimmed = String(descStr || '').trim();
  const descHtml = descTrimmed
    ? '<div class="pb-question-desc" data-pb-template="' +
      esc(descTrimmed) +
      '">' +
      esc(descTrimmed) +
      '</div>'
    : '';
  return (
    '<div class="step-content-inner">' +
    questionSpan +
    descHtml +
    innerHtml +
    '</div>'
  );
}

function createTextAdapter() {
  return createTextAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    renderControlShell,
    renderImageColumn,
    resolveAdapterSettings,
    validateTextInput,
    escapeHtmlAttr,
  });
}

export function renderRegistryMainFieldStep(field, options) {
  if (!isUnifiedFieldEngineEnabled()) return null;
  const registry = getDefaultFieldRegistry();
  const mainState = (typeof window !== 'undefined' && window.builderState?.main) || {};
  const opts = options || {};
  const pb = typeof window !== 'undefined' ? window.productBuilder : null;
  const ctx = buildFieldCtx(FIELD_MODES.MAIN, field, mainState, {
    flow: opts.flow || FIELD_FLOWS.FRESH,
    productRef: {
      image:
        opts.productRef?.image ||
        (typeof window !== 'undefined' && window.BUILDER_DATA?.mainProduct?.image) ||
        '',
    },
    pb,
    mode: FIELD_MODES.MAIN,
  });
  try {
    return registry.renderStep(ctx);
  } catch (err) {
    console.warn('[PB] renderRegistryMainFieldStep failed', err);
    return null;
  }
}

function createLongTextAdapter() {
  return createLongTextAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    renderControlShell,
    renderImageColumn,
    resolveAdapterSettings,
    validateTextInput,
  });
}

export function buildVariantFieldFromOption(option) {
  const name = String((option && option.name) || '');
  return {
    type: 'Variant',
    label: name,
    question: name,
    option: option || { name: '', values: [] },
  };
}

function buildVariantButtons(option, ctx) {
  const esc = resolveEsc(ctx);
  const optionName = String((option && option.name) || '');
  const values = (option && option.values) || [];
  const selections = resolveSelections(ctx);

  return values
    .map(function (value) {
      const resolvedVal = resolveToken(ctx, value);
      const isActive = selections[optionName] === value;
      return (
        '<button type="button" class="builder-btn variant-btn' +
        (isActive ? ' active' : '') +
        '" data-option="' +
        esc(optionName) +
        '" data-value="' +
        esc(value) +
        '" data-pb-template="' +
        esc(value) +
        '" onclick="window.productBuilder.selectVariant(this)">' +
        esc(resolvedVal) +
        '</button>'
      );
    })
    .join('');
}

function renderVariantImage(field, ctx) {
  const option = (field && field.option) || {};
  const esc = resolveEsc(ctx);
  const data = typeof window !== 'undefined' ? window.BUILDER_DATA : null;
  const item = ctx.item || (ctx.mode === FIELD_MODES.MAIN ? data?.mainProduct : null) || resolveProductRef(ctx);
  const selections = resolveSelections(ctx);
  const src = resolveVariantImageSource({ ...item, options: ctx.options || item.options || data?.optionsWithValues || [],
    variants: ctx.variants || item.variants || [], selections, optionName:option.name,
    optionValue:selections[option.name], lengthImage:ctx.mode === FIELD_MODES.MAIN ? data?.lengthImage : item.lengthImage });
  return '<div class="pb-guide-image-wrap"><img' + (src ? ' src="' + esc(src) + '"' : '') +
    ' class="pb-guide-image variant-main-image" alt="' + esc(option.name || 'Preview') +
    '" width="300" height="300" loading="lazy" data-src-key=""></div>';
}

function renderVariantControlInner(field, ctx) {
  const option = (field && field.option) || {};
  const esc = resolveEsc(ctx);
  const isMain = ctx.mode === FIELD_MODES.MAIN;
  const buttons = buildVariantButtons(option, ctx);
  const labelHtml = isMain
    ? '<span class="option-label">' + esc(String(option.name || '').toUpperCase()) + '</span>'
    : '<span class="option-label" style="margin-bottom:8px;"><span class="pb-question-text" data-pb-template="' +
      esc(option.name) +
      '">' +
      esc(option.name) +
      '</span></span>';

  return (
    '<div class="step-content-inner"><div class="variant-group" style="margin-bottom: 25px;">' +
    labelHtml +
    '<div class="variant-slider-track" data-option-name="' +
    esc(option.name) +
    '" data-required="true">' +
    buttons +
    '</div></div></div>'
  );
}

function hydrateVariantStep(stepEl, ctx) {
  if (!stepEl) return;
  const selections = resolveSelections(ctx);
  const touched = ctx.touched || {};
  stepEl.querySelectorAll('.variant-btn').forEach(function (btn) {
    const opt = btn.dataset.option;
    const val = btn.dataset.value;
    const isActive =
      ctx.mode === FIELD_MODES.SUB
        ? !!touched[opt] && selections[opt] === val
        : selections[opt] === val;
    btn.classList.toggle('active', isActive);
  });
}

export function renderRegistryVariantStep(option, options) {
  if (!isUnifiedFieldEngineEnabled()) return null;
  const opts = options || {};
  const mode = opts.mode || FIELD_MODES.MAIN;
  const stateSlice =
    opts.stateSlice ||
    (typeof window !== 'undefined' && window.builderState
      ? mode === FIELD_MODES.SUB
        ? window.builderState.sub
        : window.builderState.main
      : {});
  const field = buildVariantFieldFromOption(option);
  const registry = getDefaultFieldRegistry();
  const ctx = buildFieldCtx(mode, field, stateSlice, {
    flow: opts.flow || FIELD_FLOWS.FRESH,
    item: opts.item,
    productRef: opts.productRef,
    pb: opts.pb || (typeof window !== 'undefined' ? window.productBuilder : null),
    mode,
  });
  try {
    return registry.renderStep(ctx);
  } catch (err) {
    console.warn('[PB] renderRegistryVariantStep failed', err);
    return null;
  }
}

function createVariantAdapter() {
  return {
    id: 'variant',
    matches(field) {
      return String((field && field.type) || '').toLowerCase() === 'variant';
    },
    renderStep(ctx) {
      const field = ctx.field;
      return {
        controlHtml: renderVariantControlInner(field, ctx),
        imageHtml: renderVariantImage(field, ctx),
        stepAttrs: '',
        extraClasses: '',
      };
    },
    initStep(stepEl, ctx) {
      hydrateVariantStep(stepEl, ctx);
    },
    hydrateStep(stepEl, ctx) {
      hydrateVariantStep(stepEl, ctx);
    },
    validateStep(stepEl) {
      return !!(stepEl && stepEl.querySelector('.variant-btn.active'));
    },
    flushStep() {},
  };
}

function createChoiceAdapter() {
  return createChoiceAdapterFromPlugin({
    resolveEsc,
    resolveSelections,
    settingsToString,
    extractChoices,
    resolveToken,
    renderControlShell,
    renderImageColumn,
    resolveRuntimeParseSettings,
    validateChoiceGrid,
  });
}

function createCustomAdapter() {
  return {
    id: 'custom',
    matches(field) {
      if (isDerivedPropertiesField(field)) return false;
      return fieldTypeLower(field).includes('custom');
    },
    renderStep(ctx) {
      const field = ctx.field;
      const esc = resolveEsc(ctx);
      const settingsRaw = field.settings;
      const codeStr =
        typeof settingsRaw === 'string'
          ? settingsRaw
          : settingsRaw && settingsRaw.value
            ? settingsRaw.value
            : settingsToString(settingsRaw);
      const controlHtml =
        '<div class="pb-custom-inputs" data-custom-label="' +
        esc(field.label) +
        '" data-custom-code="' +
        esc(codeStr) +
        '"></div>';
      return {
        controlHtml,
        imageHtml:
          '<div class="pb-custom-preview" data-custom-label="' + esc(field.label) + '"></div>',
        stepAttrs: ' data-custom-field="true"',
        extraClasses: 'pb-step-custom',
      };
    },
    initStep(stepEl, ctx) {
      delegateInitStep(stepEl, '.pb-custom-inputs', function (inputsEl) {
        if (typeof window === 'undefined' || !window.PBToolkit?.init) return;
        if (inputsEl.dataset.pbtkInit === 'true') return;
        const stepId = stepEl.id || '';
        const imgId = stepId
          .replace('content-page-', 'img-page-')
          .replace('sub-content-page-', 'sub-img-page-');
        const previewEl = document.getElementById(imgId);
        const previewContainer = previewEl ? previewEl.querySelector('.pb-custom-preview') : null;
        window.PBToolkit.init(inputsEl, previewContainer, { label: ctx.field.label });
        inputsEl.dataset.pbtkInit = 'true';
      });
    },
    validateStep(stepEl) {
      if (typeof window !== 'undefined' && window.PBToolkit?.validateStep) {
        return window.PBToolkit.validateStep(stepEl);
      }
      return true;
    },
    hydrateStep(stepEl, ctx) {
      this.initStep(stepEl, ctx);
      const inst =
        typeof window !== 'undefined' && window.PBToolkit?.getInstance
          ? window.PBToolkit.getInstance(stepEl)
          : null;
      if (inst && typeof inst.hydrateFromSelections === 'function') inst.hydrateFromSelections();
    },
    flushStep(stepEl) {
      const inst =
        typeof window !== 'undefined' && window.PBToolkit?.getInstance
          ? window.PBToolkit.getInstance(stepEl)
          : null;
      if (inst && typeof inst.flushToSelections === 'function') inst.flushToSelections();
    },
  };
}

function createDesignLibraryAdapter() {
  return createDesignLibraryAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    settingsToString,
    renderControlShell,
    delegateInitStep,
  });
}

function createDerivedPropertiesAdapter() {
  return createDerivedPropertiesAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    renderControlShell,
  });
}

function createTopicMultiAdapter() {
  return createTopicMultiAdapterFromPlugin({
    resolveEsc,
    settingsToString,
    renderControlShell,
    renderImageColumn,
    validateTopicMultiGate,
  });
}

function createUploadAdapter() {
  return createUploadAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    resolveRuntimeParseSettings,
    renderControlShell,
    renderImageColumn,
    delegateInitStep,
    bindUploadWrapInteractions,
    uploadAcceptAttr,
  });
}

function createAudioAdapter() {
  return createAudioAdapterFromPlugin({
    resolveEsc,
    resolveSelections,
    settingsToString,
    resolveRuntimeParseSettings,
    renderControlShell,
    renderImageColumn,
    delegateInitStep,
    bindAudioWrapInteractions,
  });
}

function createAiImageAdapter() {
  return createAiImageAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    settingsToString,
    resolveRuntimeParseSettings,
    renderControlShell,
    bindUploadWrapInteractions,
    delegateInitStep,
  });
}

function createAiTextAdapter() {
  return createAiTextAdapterFromPlugin({
    fieldTypeLower,
    resolveEsc,
    resolveSelections,
    resolveProductRef,
    settingsToString,
    renderControlShell,
    delegateInitStep,
  });
}

export function registerComplexFieldTypes(registry) {
  registry.registerFieldType(createCustomAdapter());
  registry.registerFieldType(createDerivedPropertiesAdapter());
  registry.registerFieldType(createDesignLibraryAdapter());
  registry.registerFieldType(createTopicMultiAdapter());
  registry.registerFieldType(createAiTextAdapter());
  registry.registerFieldType(createAiImageAdapter());
  registry.registerFieldType(createAudioAdapter());
  registry.registerFieldType(createUploadAdapter());
  return registry;
}

export function registerSimpleFieldTypes(registry) {
  registry.registerFieldType(createTextAdapter());
  registry.registerFieldType(createLongTextAdapter());
  registry.registerFieldType(createChoiceAdapter());
  registry.registerFieldType(createVariantAdapter());
  return registry;
}

export function createFieldRegistry(options) {
  const opts = options || {};
  const types = [];

  function registerFieldType(def) {
    if (!def || typeof def.matches !== 'function' || typeof def.renderStep !== 'function') {
      throw new Error('registerFieldType requires matches() and renderStep()');
    }
    types.push(def);
    return def;
  }

  let pbAiTextMisrouteWarned = false;

  function shouldWarnAiTextMisroute() {
    if (typeof window === 'undefined') return false;
    if (window.Shopify && window.Shopify.designMode) return true;
    const host = window.location && window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
  }

  function warnAiTextTypeMisroute(field) {
    if (!hasAiTextSettingsSignature(field) || isAiTextFieldType(field)) return;
    if (!shouldWarnAiTextMisroute() || pbAiTextMisrouteWarned) return;
    pbAiTextMisrouteWarned = true;
    if (typeof console !== 'undefined' && typeof console.warn === 'function') {
      const label = (field && field.label) || 'field';
      const typeLabel = extractFieldTypeLabel(field) || 'unknown';
      console.warn(
        '[PB AI Text] Field "' +
          label +
          '" has AI Text settings but Type is "' +
          typeLabel +
          '". Routing to AI Text adapter — set Type to AI Text in Shopify Admin.'
      );
    }
  }

  function findAdapterById(id) {
    for (let i = 0; i < types.length; i += 1) {
      if (types[i].id === id) return types[i];
    }
    return null;
  }

  function resolveFieldType(field) {
    let matched = null;
    for (let i = 0; i < types.length; i += 1) {
      if (types[i].matches(field)) {
        matched = types[i];
        break;
      }
    }
    if (matched && matched.id === 'text' && hasAiTextSettingsSignature(field)) {
      warnAiTextTypeMisroute(field);
      const aiText = findAdapterById('ai-text');
      if (aiText) return aiText;
    }
    if (matched) return matched;
    throw new UnsupportedFieldTypeError(field && field.type);
  }

  function renderStep(ctx) {
    if (!ctx || !ctx.field) {
      throw new Error('renderStep requires ctx.field');
    }
    if (isAiTextFieldMisconfigured(ctx.field)) {
      const esc = resolveEsc(ctx);
      return {
        controlHtml: renderControlShell(ctx.field, renderAiTextMisconfigPanel(ctx.field, esc), ctx),
        imageHtml: renderImageColumn(ctx.field, ctx),
        stepAttrs: ' data-ai-text-misconfigured="true"',
        extraClasses: 'pb-step-unsupported',
      };
    }
    try {
      const adapter = resolveFieldType(ctx.field);
      return adapter.renderStep(ctx);
    } catch (err) {
      if (err instanceof UnsupportedFieldTypeError) {
        const esc = resolveEsc(ctx);
        return {
          controlHtml: renderControlShell(ctx.field, renderUnsupportedPanel(ctx.field, esc), ctx),
          imageHtml: renderImageColumn(ctx.field, ctx),
          stepAttrs: ' data-field-unsupported="true"',
          extraClasses: 'pb-step-unsupported',
        };
      }
      throw err;
    }
  }

  function initStep(stepEl, ctx) {
    const adapter = resolveFieldType(ctx.field);
    if (typeof adapter.initStep === 'function') adapter.initStep(stepEl, ctx);
  }

  function validateStep(stepEl, ctx) {
    const adapter = resolveFieldType(ctx.field);
    if (typeof adapter.validateStep === 'function') {
      return adapter.validateStep(stepEl, ctx);
    }
    return true;
  }

  function hydrateStep(stepEl, ctx) {
    const adapter = resolveFieldType(ctx.field);
    if (typeof adapter.hydrateStep === 'function') adapter.hydrateStep(stepEl, ctx);
  }

  function flushStep(stepEl, ctx) {
    const adapter = resolveFieldType(ctx.field);
    if (typeof adapter.flushStep === 'function') adapter.flushStep(stepEl, ctx);
  }

  function hydrateContainer(containerSelector, options) {
    return runRegistryContainerHydrate(registry, containerSelector, options);
  }

  const registry = {
    registerFieldType,
    resolveFieldType,
    renderStep,
    initStep,
    validateStep,
    hydrateStep,
    flushStep,
    hydrateContainer,
    listFieldTypes() {
      return types.map(function (t) {
        return t.id || 'anonymous';
      });
    },
  };

  if (opts.withSimpleAdapters !== false) {
    registerSimpleFieldTypes(registry);
  }

  if (opts.withComplexAdapters !== false) {
    registerComplexFieldTypes(registry);
  }

  return registry;
}

export function getDefaultFieldRegistry() {
  if (!getDefaultFieldRegistry._instance) {
    getDefaultFieldRegistry._instance = createFieldRegistry();
  }
  return getDefaultFieldRegistry._instance;
}

export function inferSubFieldFlow(subState) {
  const s = subState || {};
  if (s._fieldFlow && Object.values(FIELD_FLOWS).includes(s._fieldFlow)) return s._fieldFlow;
  if (s.isQueueItem) return FIELD_FLOWS.BUNDLE_ROW_EDIT;
  const sel = s.selections || {};
  if (Object.keys(sel).length > 0) return FIELD_FLOWS.CART_EDIT;
  return FIELD_FLOWS.FRESH;
}

export function inferFieldFlow(stateSlice, hints) {
  const h = hints || {};
  if (h.flow && Object.values(FIELD_FLOWS).includes(h.flow)) return h.flow;
  const s = stateSlice || {};
  if (s._fieldFlow && Object.values(FIELD_FLOWS).includes(s._fieldFlow)) return s._fieldFlow;
  if (h.isQueueItem || s.isQueueItem) return FIELD_FLOWS.BUNDLE_ROW_EDIT;
  if (h.cartEdit || (typeof window !== 'undefined' && window.builderState?.cartEdit)) {
    return FIELD_FLOWS.CART_EDIT;
  }
  if (h.draftResume || h.fromDraft) return FIELD_FLOWS.DRAFT_RESUME;
  if (h.mode === FIELD_MODES.SUB) return inferSubFieldFlow(s);
  return FIELD_FLOWS.FRESH;
}

function escapeSelectorValue(value) {
  const str = String(value ?? '');
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(str);
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function findStepElementForField(root, field, options) {
  if (!root || !field?.label) return null;
  const label = field.label;
  const escLabel = escapeSelectorValue(label);
  const stepMap = options?.stepMap;

  if (stepMap) {
    for (const key of Object.keys(stepMap)) {
      const entry = stepMap[key];
      if (entry?.type === 'config' && entry.field?.label === label) {
        return root.querySelector('#sub-content-page-' + key);
      }
      if (entry?.type === 'variant' && entry.option?.name === label) {
        return root.querySelector('#sub-content-page-' + key);
      }
    }
  }

  let el = root.querySelector('.pb-step[data-topic-label="' + escLabel + '"]');
  if (el) return el;

  el = root.querySelector('[data-topic-label="' + escLabel + '"]');
  if (el) return el.closest('.pb-step') || el;

  const controlSelectors = [
    '.pb-dl-wrap[data-pb-dl-label="' + escLabel + '"]',
    '.pb-ai-image-wrap[data-label="' + escLabel + '"]',
    '.pb-ai-text-wrap[data-label="' + escLabel + '"]',
    '.pb-upload-wrap[data-label="' + escLabel + '"]',
    '.pb-audio-wrap[data-label="' + escLabel + '"]',
    '.pb-custom-inputs[data-custom-label="' + escLabel + '"]',
    '.builder-input[data-label="' + escLabel + '"]',
    '.builder-btn.meta-btn[data-label="' + escLabel + '"]',
    '.variant-slider-track[data-option-name="' + escLabel + '"]',
  ];
  for (let i = 0; i < controlSelectors.length; i += 1) {
    const node = root.querySelector(controlSelectors[i]);
    if (node) {
      const step = node.closest('.pb-step');
      if (step) return step;
    }
  }
  return null;
}

export function buildFieldCtx(mode, field, stateSlice, extras) {
  const slice = stateSlice || {};
  const productBuilder =
    (extras && extras.pb) || (typeof window !== 'undefined' ? window.productBuilder : null);
  const flow =
    (extras && extras.flow) ||
    inferFieldFlow(slice, { ...(extras || {}), mode: mode || FIELD_MODES.MAIN });
  return {
    mode: mode || FIELD_MODES.MAIN,
    flow,
    field,
    selections: slice.selections || {},
    options: slice.options,
    variants: slice.variants,
    topicSelections: slice.topicSelections || {},
    touched: slice.touched || {},
    item: (extras && extras.item) || null,
    productRef:
      (extras && extras.productRef) || { image: (extras && extras.item && extras.item.image) || '' },
    esc: escapeHtmlAttr,
    parseSettings:
      productBuilder && productBuilder.parseSettings
        ? productBuilder.parseSettings.bind(productBuilder)
        : parseSettings,
    resolveToken:
      productBuilder && productBuilder.resolvePersonalizationTokens
        ? function (value) {
            return productBuilder.resolvePersonalizationTokens(value, slice);
          }
        : undefined,
    extractChoices:
      productBuilder && productBuilder.extractChoices
        ? productBuilder.extractChoices.bind(productBuilder)
        : extractChoices,
  };
}

export function buildSubFieldCtx(field, item, subState, pb) {
  return buildFieldCtx(FIELD_MODES.SUB, field, subState, {
    item,
    pb,
    flow: inferSubFieldFlow(subState),
    productRef: { image: (item && item.image) || '' },
  });
}

function runRegistryContainerHydrate(registry, containerSelector, options) {
  if (typeof document === 'undefined') return false;
  if (!isUnifiedFieldEngineEnabled()) return false;

  const opts = options || {};
  const fields = opts.fields;
  const stateSlice = opts.stateSlice;
  if (!fields?.length || !stateSlice) return false;

  const root =
    typeof containerSelector === 'string'
      ? document.querySelector(containerSelector)
      : containerSelector;
  if (!root) return false;

  const selectorStr =
    typeof containerSelector === 'string' ? containerSelector : '#' + (root.id || '');
  const mode =
    opts.mode ||
    (selectorStr.indexOf('sub-flow') !== -1 ? FIELD_MODES.SUB : FIELD_MODES.MAIN);
  const flow = opts.flow || inferFieldFlow(stateSlice, { ...opts, mode });
  const pb = typeof window !== 'undefined' ? window.productBuilder : null;
  const stepMap =
    opts.stepMap ||
    (mode === FIELD_MODES.SUB && typeof window !== 'undefined'
      ? window.builderState?.sub?.stepMap
      : null);

  if (pb?.inferTopicSelectionsFromAnswers) {
    pb.inferTopicSelectionsFromAnswers(stateSlice, fields);
  }

  const productRef = opts.productRef || { image: opts.item?.image || '' };

  fields.forEach(function (field) {
    if (!field) return;
    const stepEl = findStepElementForField(root, field, { stepMap });
    if (!stepEl) return;
    const ctx = buildFieldCtx(mode, field, stateSlice, {
      flow,
      productRef,
      item: opts.item,
      pb,
      mode,
    });
    registry.hydrateStep(stepEl, ctx);
  });

  root.querySelectorAll('.pb-step[data-topic-multi="true"]').forEach(function (stepEl) {
    if (pb?.buildTopicSteps) pb.buildTopicSteps(stepEl);
  });

  const contentSteps = root.querySelectorAll('.pb-bottom-wrapper .pb-step-viewport .pb-step');
  if (contentSteps.length) stateSlice.maxSteps = contentSteps.length;

  if (selectorStr === '#main-flow-container' && pb?.syncMainMaxStepsFromDom) {
    pb.syncMainMaxStepsFromDom();
  }

  root.querySelectorAll('.pb-guide-image').forEach(image => mountStoreMedia(image, { intrinsicRatio: true }));
  return true;
}

function mergeStepAttrsOntoElement(stepEl, stepAttrs) {
  if (!stepEl || !stepAttrs) return;
  String(stepAttrs)
    .trim()
    .split(/\s+/)
    .forEach(function (pair) {
      if (!pair) return;
      const eq = pair.indexOf('=');
      if (eq === -1) return;
      const key = pair.slice(0, eq);
      const val = pair.slice(eq + 1).replace(/^"|"$/g, '');
      if (key) stepEl.setAttribute(key, val);
    });
}

function resolveImageMountForStep(root, stepEl, mode) {
  if (!stepEl) return null;
  const local = stepEl.querySelector('[data-pb-field-mount-image]');
  if (local) return local;
  if (mode !== FIELD_MODES.MAIN || !root) return null;
  const match = String(stepEl.id || '').match(/content-page-(\d+)$/);
  if (!match) return null;
  const imgStep = root.querySelector('#img-page-' + match[1]);
  return imgStep ? imgStep.querySelector('[data-pb-field-mount-image]') : null;
}

function parseMountField(mountEl) {
  if (!mountEl) return null;
  try {
    const field = JSON.parse(mountEl.getAttribute('data-field') || '{}');
    return field && field.type ? field : null;
  } catch (e) {
    return null;
  }
}

/**
 * Post-mount field lifecycle — init every registry field + layout contract (PB-FND-28).
 * Replaces per-path applyInputSettings for adapter-owned presentation.
 */
export function finalizeFieldPresentation(containerSelector, options) {
  if (typeof document === 'undefined') return 0;
  if (!isUnifiedFieldEngineEnabled()) return 0;

  const opts = options || {};
  const root =
    typeof containerSelector === 'string'
      ? document.querySelector(containerSelector)
      : containerSelector;
  if (!root) return 0;

  const registry = opts.registry || getDefaultFieldRegistry();
  const selectorStr =
    typeof containerSelector === 'string' ? containerSelector : '#' + (root.id || '');
  const mode =
    opts.mode ||
    (selectorStr.indexOf('sub-flow') !== -1 ? FIELD_MODES.SUB : FIELD_MODES.MAIN);
  const stateSlice =
    opts.stateSlice ||
    (typeof window !== 'undefined' && window.builderState
      ? mode === FIELD_MODES.SUB
        ? window.builderState.sub
        : window.builderState.main
      : {});
  const flow = opts.flow || inferFieldFlow(stateSlice, { ...opts, mode });
  const pb = opts.pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  const productRef = opts.productRef || {
    image:
      opts.item?.image ||
      (typeof window !== 'undefined' ? window.BUILDER_DATA?.mainProduct?.image : '') ||
      '',
  };
  const item =
    opts.item ||
    (mode === FIELD_MODES.SUB ? resolveSubFlowItem(stateSlice) : window.BUILDER_DATA?.mainProduct);

  let finalized = 0;
  root.querySelectorAll('[data-pb-field-mount]').forEach(function (mountEl) {
    const field = parseMountField(mountEl);
    const stepEl = mountEl.closest('.pb-step');
    if (!field || !stepEl) return;

    const ctx = buildFieldCtx(mode, field, stateSlice, {
      flow,
      productRef,
      item,
      pb,
      mode,
    });
    registry.initStep(stepEl, ctx);

    const imgMount = resolveImageMountForStep(root, stepEl, mode);
    applyFieldLayoutContract(field, stepEl, imgMount);
    finalized += 1;
  });

  const data = typeof window !== 'undefined' ? window.BUILDER_DATA : null;
  const fieldConfig =
    mode === FIELD_MODES.SUB
      ? item?.config || stateSlice?.currentConfig || []
      : data?.shopifyBundle === true && Array.isArray(data.shopifyBundleMergedPersonalization)
        ? data.shopifyBundleMergedPersonalization
        : data?.mainProduct?.config || data?.mainProduct?.personalization || [];
  finalizeDerivedProperties(root, fieldConfig, stateSlice?.selections || {});

  root.querySelectorAll('.pb-guide-image').forEach(image => mountStoreMedia(image, { intrinsicRatio: true }));
  return finalized;
}

/**
 * Fill [data-pb-field-mount] placeholders from registry when unifiedFieldEngine is on.
 * Liquid steps remain the fallback when the flag is off (PB-FP-5 phase B).
 */
export function mountRegistryFieldSteps(containerSelector, options) {
  if (typeof document === 'undefined') return 0;
  if (!isUnifiedFieldEngineEnabled()) return 0;

  const opts = options || {};
  const root =
    typeof containerSelector === 'string'
      ? document.querySelector(containerSelector)
      : containerSelector;
  if (!root) return 0;

  const registry = opts.registry || getDefaultFieldRegistry();
  const selectorStr =
    typeof containerSelector === 'string' ? containerSelector : '#' + (root.id || '');
  const mode =
    opts.mode ||
    (selectorStr.indexOf('sub-flow') !== -1 ? FIELD_MODES.SUB : FIELD_MODES.MAIN);
  const stateSlice =
    opts.stateSlice ||
    (typeof window !== 'undefined' && window.builderState
      ? mode === FIELD_MODES.SUB
        ? window.builderState.sub
        : window.builderState.main
      : {});
  const flow = opts.flow || inferFieldFlow(stateSlice, { ...opts, mode });
  const productRef = opts.productRef || { image: opts.item?.image || '' };
  let mounted = 0;

  root.querySelectorAll('[data-pb-field-mount]').forEach(function (mountEl) {
    let field;
    try {
      field = JSON.parse(mountEl.getAttribute('data-field') || '{}');
    } catch (e) {
      return;
    }
    if (!field || !field.type) return;

    const ctx = buildFieldCtx(mode, field, stateSlice, {
      flow,
      productRef,
      item: opts.item,
      pb: opts.pb,
      mode,
    });
    const out = registry.renderStep(ctx);
    window.pbAudioEngine?.disposeFieldScope(mountEl);
    mountEl.innerHTML = out.controlHtml;

    const stepEl = mountEl.closest('.pb-step');
    if (stepEl && out.stepAttrs) mergeStepAttrsOntoElement(stepEl, out.stepAttrs);
    if (stepEl && out.extraClasses) {
      out.extraClasses.split(/\s+/).forEach(function (cls) {
        if (cls) stepEl.classList.add(cls);
      });
    }

    const imgMount = resolveImageMountForStep(root, stepEl, mode);
    if (imgMount && out.imageHtml) {
      imgMount.innerHTML = out.imageHtml;
      imgMount.querySelectorAll('.pb-guide-image').forEach(image => mountStoreMedia(image, { intrinsicRatio: true }));
    }
    applyFieldLayoutContract(field, stepEl, imgMount);

    mounted += 1;
  });

  return mounted;
}

function resolveSubFlowItem(subState) {
  const s = subState || {};
  if (typeof window === 'undefined') return null;
  if (s.isQueueItem) {
    const bundle = (window.BUILDER_DATA?.bundles || []).find(
      (b) => String(b?.id) === String(window.builderState?.bundle?.selectedId)
    );
    const idx = parseInt(s.currentId, 10);
    return bundle?.items?.[idx] || null;
  }
  return s.currentId != null ? window.builderState?.addonsDB?.[s.currentId] : null;
}

function install(PB) {
  if (!PB) return;
  if (PB._pbFieldRuntimeInstalled && typeof PB.getFieldRegistry === 'function') return;
  PB._pbFieldRuntimeInstalled = true;
  PB.isUnifiedFieldEngineEnabled = function () {
    return isUnifiedFieldEngineEnabled();
  };
  PB.createFieldRegistry = createFieldRegistry;
  PB.getFieldRegistry = getDefaultFieldRegistry;
  PB.fieldRuntime = getDefaultFieldRegistry();
  PB.inferSubFieldFlow = inferSubFieldFlow;
  PB.buildSubFieldCtx = function (field, item) {
    const subState =
      typeof window !== 'undefined' && window.builderState ? window.builderState.sub : {};
    return buildSubFieldCtx(field, item, subState, PB);
  };
  PB.initRegistrySubStep = function (stepEl, field, item) {
    if (!isUnifiedFieldEngineEnabled()) return;
    if (!stepEl || !field) return;
    const registry = getDefaultFieldRegistry();
    const subState = window.builderState?.sub || {};
    const ctx = buildSubFieldCtx(field, item || resolveSubFlowItem(subState) || {}, subState, PB);
    registry.initStep(stepEl, ctx);
  };
  PB.hydrateFieldContainer = function (containerSelector, options) {
    if (!isUnifiedFieldEngineEnabled()) return false;
    return getDefaultFieldRegistry().hydrateContainer(containerSelector, options);
  };
  PB.hydrateRegistrySubConfig = function (item) {
    if (!isUnifiedFieldEngineEnabled()) return false;
    const subState = window.builderState?.sub || {};
    const itemRef = item || resolveSubFlowItem(subState) || {};
    return PB.hydrateFieldContainer('#sub-flow-container', {
      flow: inferSubFieldFlow(subState),
      fields: subState.currentConfig || itemRef.config || [],
      stateSlice: subState,
      item: itemRef,
      mode: FIELD_MODES.SUB,
    });
  };
  PB.activateRegistrySubStep = function (stepEl) {
    if (!isUnifiedFieldEngineEnabled()) return;
    if (!stepEl || typeof PB.getSubStepEntry !== 'function') return;
    const entry = PB.getSubStepEntry(stepEl);
    if (!entry || entry.type !== 'config' || !entry.field) return;
    PB.initRegistrySubStep(stepEl, entry.field, resolveSubFlowItem(window.builderState?.sub));
  };
  PB.initRegistryMainStep = function (stepEl, field, options) {
    if (!isUnifiedFieldEngineEnabled()) return;
    if (!stepEl || !field) return;
    const registry = getDefaultFieldRegistry();
    const mainState = window.builderState?.main || {};
    const ctx = buildFieldCtx(FIELD_MODES.MAIN, field, mainState, {
      flow: options?.flow,
      productRef: {
        image:
          options?.productRef?.image ||
          window.BUILDER_DATA?.mainProduct?.image ||
          '',
      },
      pb: PB,
      mode: FIELD_MODES.MAIN,
    });
    registry.initStep(stepEl, ctx);
  };
  PB.activateRegistryMainStep = function (stepEl, options) {
    if (!isUnifiedFieldEngineEnabled()) return;
    if (!stepEl) return;
    const mountEl = stepEl.querySelector('[data-pb-field-mount]');
    const field = parseMountField(mountEl);
    if (!field) return;
    PB.initRegistryMainStep(stepEl, field, options);
  };
  PB.mountRegistryFieldSteps = function (containerSelector, options) {
    if (!isUnifiedFieldEngineEnabled()) return 0;
    return mountRegistryFieldSteps(containerSelector, options);
  };
  PB.mountRegistryMainFieldMounts = function (options) {
    return PB.mountRegistryFieldSteps('#main-flow-container', {
      ...(options || {}),
      mode: FIELD_MODES.MAIN,
    });
  };
  PB.finalizeFieldPresentation = function (containerSelector, options) {
    if (!isUnifiedFieldEngineEnabled()) return 0;
    return finalizeFieldPresentation(containerSelector, {
      ...(options || {}),
      pb: PB,
    });
  };
  PB.buildLongTextFieldHtml = function (opts) {
    return buildLongTextFieldHtmlFromPlugin(this, opts);
  };
  PB.renderRegistryMainFieldStep = function (field, options) {
    return renderRegistryMainFieldStep(field, options);
  };
  PB.renderRegistryVariantStep = function (option, options) {
    return renderRegistryVariantStep(option, {
      ...(options || {}),
      pb: PB,
    });
  };
  PB.buildVariantFieldFromOption = buildVariantFieldFromOption;
  PB.resolveVariantImageSource = resolveVariantImageSource;
  PB.resolveVariantMediaProduct = function () {
    return window.builderState?.mode === FIELD_MODES.SUB
      ? resolveSubFlowItem(window.builderState.sub) || {}
      : window.BUILDER_DATA?.mainProduct || {};
  };
  PB.flushRegistryFlowFields = function (containerSelector) {
    if (!isUnifiedFieldEngineEnabled()) return;
    const root =
      typeof containerSelector === 'string'
        ? document.querySelector(containerSelector)
        : containerSelector;
    if (!root || typeof PB.getSubStepEntry !== 'function') return;
    const registry = getDefaultFieldRegistry();
    const selectorStr =
      typeof containerSelector === 'string' ? containerSelector : '#' + (root.id || '');
    const isMain = selectorStr.indexOf('main-flow') !== -1;
    const stateSlice = isMain
      ? window.builderState?.main || {}
      : window.builderState?.sub || {};
    const item = isMain
      ? window.BUILDER_DATA?.mainProduct || {}
      : resolveSubFlowItem(stateSlice) || {};
    const mode = isMain ? FIELD_MODES.MAIN : FIELD_MODES.SUB;
    root.querySelectorAll('.pb-step').forEach(function (stepEl) {
      const entry = PB.getSubStepEntry(stepEl);
      if (entry && entry.type === 'config' && entry.field) {
        const ctx = isMain
          ? buildFieldCtx(mode, entry.field, stateSlice, { item, pb: PB, mode })
          : buildSubFieldCtx(entry.field, item, stateSlice, PB);
        registry.flushStep(stepEl, ctx);
        return;
      }
      if (entry && entry.type === 'variant' && entry.option) {
        const variantField = buildVariantFieldFromOption(entry.option);
        const ctx = isMain
          ? buildFieldCtx(mode, variantField, stateSlice, { item, pb: PB, mode })
          : buildSubFieldCtx(variantField, item, stateSlice, PB);
        registry.flushStep(stepEl, ctx);
        return;
      }
      if (stepEl?.dataset?.topicMulti === 'true') {
        registry.flushStep(
          stepEl,
          buildFieldCtx(mode, { type: 'TopicMulti', label: stepEl.dataset.topicLabel || '' }, stateSlice, {
            item,
            pb: PB,
            mode,
          })
        );
      }
    });
  };
}

function tryInstall() {
  if (typeof window === 'undefined' || !window.productBuilder) return false;
  install(window.productBuilder);
  return true;
}

/** Re-attach field registry after cart edit remounts productBuilder (ES module cache skips re-eval). */
export function ensureFieldRuntimeInstalled(pb) {
  const target = pb || (typeof window !== 'undefined' ? window.productBuilder : null);
  if (!target) return false;
  install(target);
  return typeof target.getFieldRegistry === 'function';
}

if (typeof window !== 'undefined') {
  window.__pbEnsureFieldRuntimeInstalled = ensureFieldRuntimeInstalled;
  if (!tryInstall()) {
    document.addEventListener('DOMContentLoaded', tryInstall);
    document.addEventListener('builder:ready', tryInstall);
  }
}
